import React, { useEffect, useState } from "react";
import { SectionTitle } from "./_components/SectionTitle";
import { CustomAccordion } from "./_components/CustomAccordion";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "src/components/ui/breadcrumb";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { GraduationCap, Layers, MonitorPlay } from "lucide-react";
import NumberCounter from "./../../components/common/NumberCounter";
import MainButton from "../../components/MainButton";
import TeacherCard from "./_components/TeacherCard";
import { BaseUrlApi, ErrorMessage } from "../../lib/api";
import { toast } from "sonner";
import axios from "axios";
import { useAPI } from "../../context/APIContext";

export default function MainAboutUs() {
  const { categoriesItems } = useAPI();
  const { categories, loadingCategories, getAllCategories } = categoriesItems;

  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);

  const fetchTeachers = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      const allTeachers = data.data.filter((user) => user?.role === "teacher");
      const allStudents = data.data.filter((user) => user?.role === "student");
      setStudents(allStudents);
      setTeachers(allTeachers);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") {
        toast("Error", { description: result });
      }
    } finally {
      setLoadingTeachers(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  return (
    <div className="space-y-20 max-w-7xl mx-auto px-4 mt-14">
      <div className="space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">الصفحة الرئيسية</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbPage className="font-semibold">من نحن</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div
          className="h-fit w-full bg-cover bg-top rounded-3xl text-white flex items-center justify-center"
          style={{
            backgroundImage: "url('/board.jpg')",
          }}
        >
          <div className="p-6 md:p-10 flex flex-col justify-between gap-6 md:gap-10 text-black lg:w-1/2 me-auto">
            <h1 className="text-3xl md:text-6xl font-bold">من نحن</h1>
            <p className="text-base md:text-xl">
              منصة تعليمية عربية تهدف إلى إعادة تعريف مفهوم التعلم، من خلال
              تقديم محتوى رقمي تفاعلي عالي الجودة، مبني على أسس علمية وتربوية
              عصرية. نحن نؤمن بأن كل طالب لديه القدرة على التميز، إذا توفرت له
              البيئة المناسبة والدعم الصحيح.
            </p>
            <p className="text-base md:text-xl">
              <b>رؤيتنا:</b> أن نكون المنصة التعليمية الأكثر تأثيرًا في العالم
              العربي، من خلال تقديم تجربة تعليمية رقمية متكاملة. <br />
              <b>رسالتنا:</b> تمكين المتعلمين من تطوير مهاراتهم وبناء مستقبلهم
              عبر محتوى تفاعلي، موجه، ومبني على أسس تربوية متينة.
            </p>
            <p className="font-bold text-base md:text-xl">
              تعرف علي فريقنا و أشهر معلمي المنصة :
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <CounterCard
          Icon={MonitorPlay}
          title={"عدد المعلمين"}
          subTitle={"معلم"}
          number={teachers.length}
        />
        <CounterCard
          Icon={GraduationCap}
          title={"عدد الطلاب"}
          subTitle={"طالب"}
          number={students.length}
        />
        <CounterCard
          Icon={Layers}
          title={"عدد الاقسام"}
          subTitle={"أقسام متنوعة"}
          number={categories.length}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-center">
        <div className="space-y-8 col-span-1">
          <div className="font-bold text-3xl">
            مهمتنا هي إطلاق طاقات الإبداع في التعليم
          </div>
          <div className="text-lg">
            نحن نؤمن أن التعليم الحقيقي يبدأ عندما تُمنح العقول المساحة لتفكر
            بحرية وتبتكر. في منصتنا، نخلق بيئة تعليمية محفّزة تُزهر فيها
            الأفكار، وتولد منها حلول ومهارات تغير المستقبل. نرعى المواهب، وندعم
            الشغف، لنُخرج أفضل ما في كل متعلم.
          </div>
        </div>
        <div className="col-span-1 lg:col-span-2 flex items-end justify-end">
          <div className="h-[440px] w-full lg:w-[700px]">
            <img
              loading="lazy"
              src="metion.jpg"
              alt="metion"
              className="object-cover object-top h-full w-full rounded-2xl"
            />
          </div>
        </div>
      </div>

      <div className="space-y-10">
        <div className="space-y-4 text-center">
          <SectionTitle title={"فريق العمل"} />
          <p className="text-lg text-muted-foreground">
            نحن فريق متكامل من التربويين، والمصممين، والمطورين، والمبدعين، نعمل
            بشغف لجعل التعليم أكثر سهولة ومتعة.
          </p>
        </div>

        <TeacherCard teachers={teachers} loading={loadingTeachers} />
      </div>

      <div className="space-y-10">
        <div className="space-y-4 text-center">
          <SectionTitle title={"أشهر المعلمين لدينا"} />
          <p className="text-lg text-muted-foreground">
            نفتخر بتعاوننا مع نخبة من أفضل المعلمين في العالم العربي، الذين
            يملكون خبرة واسعة في مجالاتهم ويقدّمون محتوى تعليميًا مميزًا:
          </p>
        </div>

        <TeacherCard teachers={teachers} loading={loadingTeachers} />
      </div>
    </div>
  );
}

const CounterCard = ({ Icon, title, subTitle, number }) => (
  <Card className="p-0 overflow-hidden">
    <div className="bg-gradient-to-t from-blue-700 to-blue-500 text-white h-10 flex items-center gap-2 justify-center">
      <Icon /> {title}
    </div>

    <div className="font-bold text-3xl p-6 text-center">
      + <NumberCounter targetNumber={number} /> {subTitle}
    </div>
  </Card>
);
